import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FiChevronLeft, FiMoreHorizontal } from "react-icons/fi";
import { db } from "../../../firebase/firebase.utils";
import Avatar from "../../retro/avatar/avatar.component";
import {
  fetchUsersByIds,
  firstProfilePhoto,
  removeConnection,
} from "./profileApi";
import {
  blockUser,
  unblockUser,
  setConnectionMuted,
} from "../../../firebase/chatPreferences";
const TABS = ["connections", "muted", "blocked"];

export default function Connections() {
  const { uid: routeUid } = useParams();
  const currentUser = useSelector((s) => s.user.currentUser);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const uid = routeUid || currentUser?.uid;
  const own = uid === currentUser?.uid;
  const active =
    own && TABS.includes(params.get("tab")) ? params.get("tab") : "connections";
  const [connections, setConnections] = useState([]);
  const [muted, setMuted] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [mutedIds, setMutedIds] = useState([]);
  const [loading, setLoading] = useState({ connections: true, privacy: true });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const lock = useRef(false);
  useEffect(() => {
    let alive = true,
      generation = 0;
    setConnections([]);
    setError("");
    if (!uid || !own) {
      if (uid && !own) setError("Connections are private.");
      setLoading((x) => ({ ...x, connections: false }));
      return;
    }
    setLoading((x) => ({ ...x, connections: true }));
    const off = db
      .collection("connections")
      .where("uids", "array-contains", uid)
      .limit(300)
      .onSnapshot(
        async (snap) => {
          const request = ++generation;
          try {
            const rows = snap.docs
              .map((d) => ({
                connectionId: d.id,
                otherUid: (d.data().uids || []).find((id) => id !== uid),
                createdAt: d.data().createdAt,
              }))
              .filter((x) => x.otherUid)
              .sort(
                (a, b) =>
                  (b.createdAt?.toMillis?.() || 0) -
                  (a.createdAt?.toMillis?.() || 0),
              );
            const users = await fetchUsersByIds(rows.map((x) => x.otherUid));
            const byId = new Map(users.map((u) => [u.uid, u]));
            if (alive && request === generation)
              setConnections(
                rows
                  .map((r) => ({ ...byId.get(r.otherUid), ...r }))
                  .filter((r) => r.uid),
              );
          } catch {
            if (alive && request === generation)
              setError("Could not load connections.");
          } finally {
            if (alive && request === generation)
              setLoading((x) => ({ ...x, connections: false }));
          }
        },
        () => {
          if (alive) {
            setError("These connections are unavailable.");
            setLoading((x) => ({ ...x, connections: false }));
          }
        },
      );
    return () => {
      alive = false;
      off();
    };
  }, [uid, own]);
  useEffect(() => {
    let alive = true,
      generation = 0;
    setMuted([]);
    setBlocked([]);
    setMutedIds([]);
    if (!own || !uid) {
      setLoading((x) => ({ ...x, privacy: false }));
      return;
    }
    setLoading((x) => ({ ...x, privacy: true }));
    const off = db
      .collection("users")
      .doc(uid)
      .onSnapshot(
        async (snap) => {
          const request = ++generation;
          const u = snap.data() || {};
          const ids = Array.isArray(u.mutedUsers) ? u.mutedUsers : [];
          try {
            const [m, b] = await Promise.all([
              fetchUsersByIds(ids),
              fetchUsersByIds(
                Array.isArray(u.blockedUsers) ? u.blockedUsers : [],
              ),
            ]);
            if (alive && request === generation) {
              setMutedIds(ids);
              setMuted(m);
              setBlocked(b);
            }
          } catch {
            if (alive && request === generation)
              setError("Could not load privacy settings.");
          } finally {
            if (alive && request === generation)
              setLoading((x) => ({ ...x, privacy: false }));
          }
        },
        () => {
          if (alive) {
            setError("Could not load privacy settings.");
            setLoading((x) => ({ ...x, privacy: false }));
          }
        },
      );
    return () => {
      alive = false;
      off();
    };
  }, [own, uid]);
  const data =
    active === "muted" ? muted : active === "blocked" ? blocked : connections;
  const pending =
    active === "connections" ? loading.connections : loading.privacy;
  const run = async (id, action) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(id);
    try {
      await action();
    } catch (e) {
      window.alert(e.message || "The action could not be completed.");
    } finally {
      lock.current = false;
      setBusy(null);
    }
  };
  return (
    <main className="social-route-page connections-page">
      <header className="social-page-header">
        <button
          type="button"
          className="icon-button"
          aria-label="Back"
          onClick={() => navigate(-1)}
        >
          <FiChevronLeft />
        </button>
        <div>
          <h1>Connections</h1>
          <p>
            {own
              ? "Manage your people and privacy."
              : "People connected to this profile."}
          </p>
        </div>
        {own && (
          <button type="button" onClick={() => navigate("/people")}>
            Find people
          </button>
        )}
      </header>
      <div className="connections-tabs">
        {(own ? TABS : ["connections"]).map((tab) => (
          <button
            type="button"
            key={tab}
            className={active === tab ? "active" : ""}
            onClick={() => setParams(tab === "connections" ? {} : { tab })}
          >
            {tab[0].toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      {error && <p role="alert">{error}</p>}
      <div className="people-list connections-list">
        {pending && <div className="people-list__empty">Loading…</div>}
        {!pending &&
          data.map((person) => (
            <div className="connection-row" key={person.uid}>
              <button
                type="button"
                className="connection-row__person"
                onClick={() =>
                  navigate(`/profile/${person.uid}`, {
                    state: { user: person },
                  })
                }
              >
                <Avatar
                  user={person}
                  src={firstProfilePhoto(person.imageUrl)}
                  size={46}
                />
                <span>
                  <strong>{person.userName || "Unknown"}</strong>
                  {person.headline && <small>{person.headline}</small>}
                </span>
              </button>
              {own && (
                <details className="row-menu">
                  <summary aria-label="Connection actions">
                    <FiMoreHorizontal />
                  </summary>
                  <div>
                    <button
                      type="button"
                      onClick={() => navigate(`/profile/${person.uid}`)}
                    >
                      View profile
                    </button>
                    {active !== "blocked" && (
                      <button
                        type="button"
                        disabled={!!busy}
                        onClick={() =>
                          run(person.uid, () =>
                            setConnectionMuted(
                              uid,
                              person.uid,
                              !mutedIds.includes(person.uid),
                            ),
                          )
                        }
                      >
                        {mutedIds.includes(person.uid) ? "Unmute" : "Mute"}
                      </button>
                    )}
                    {active === "connections" && (
                      <button
                        type="button"
                        disabled={!!busy}
                        onClick={() => {
                          if (window.confirm("Remove this connection?"))
                            run(person.uid, () =>
                              removeConnection(uid, person.uid),
                            );
                        }}
                      >
                        Remove connection
                      </button>
                    )}
                    <button
                      type="button"
                      className="danger"
                      disabled={!!busy}
                      onClick={() => {
                        if (
                          active === "blocked" ||
                          window.confirm(
                            "Block this person? This removes your connection and prevents direct messages and calls. Shared groups stay unchanged.",
                          )
                        )
                          run(person.uid, () =>
                            (active === "blocked" ? unblockUser : blockUser)(
                              uid,
                              person.uid,
                            ),
                          );
                      }}
                    >
                      {active === "blocked" ? "Unblock" : "Block"}
                    </button>
                  </div>
                </details>
              )}
              {busy === person.uid && (
                <span className="connection-row__busy">…</span>
              )}
            </div>
          ))}
        {!pending && !data.length && (
          <div className="people-list__empty">
            {active === "blocked"
              ? "No blocked users."
              : active === "muted"
                ? "No muted users."
                : "No connections yet."}
          </div>
        )}
      </div>
    </main>
  );
}
